import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef, useState } from "react"
import { Minimize2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { HeroInfoPopover } from "./HeroInfoPopover"
import { HeroVorgangName } from "./HeroVorgangName"
import { saveDraft, removeDraft, newDraftId, type CalendarDraft } from "@/lib/draftModals"
import { CreateContactModal } from "./CreateContactModal"
import { toast } from "./Toast"
import { darfTerminOhneMitarbeiterBuchen } from "@/lib/aufgabeVerknuepfung"

interface CalOptions {
  categories: { id: number; name: string }[]
  partners: { id: number; name: string }[]
  monteure: { id: number; name: string }[]
  resources: { id: number; name: string }[]
  /** Kategorie des Mandanten (`tenants.hero_calendar_category_id`) — Vorauswahl. */
  default_category_id?: number | null
}

/** Neuer Termin — inkl. Minimieren-in-Draft-Store (persistiert in localStorage,
 *  Pill wird global in AppLayout/MinimizedDraftsBar gerendert). */
export function CalendarCreateModal({
  open, onClose, tenant, initialStart, initialEnd, initialTitle, initialDescription, initialCategoryId, initialPartnerIds, initialProjectId, draftId,
}: {
  open: boolean
  onClose: () => void
  tenant: string
  initialStart?: string
  initialEnd?: string
  initialTitle?: string
  initialDescription?: string
  initialCategoryId?: string
  initialPartnerIds?: number[]
  initialProjectId?: string
  /** Wenn gesetzt → Modal ist Wiederherstellung eines minimierten Drafts. Bei Save/Close wird der Draft entfernt. */
  draftId?: string
}) {
  const qc = useQueryClient()
  const [title, setTitle] = useState("Neuer Termin")
  const [start, setStart] = useState(initialStart || "")
  const [end, setEnd] = useState(initialEnd || "")
  const [allDay, setAllDay] = useState(false)
  const [recurrence, setRecurrence] = useState<"none" | "daily" | "weekdays" | "weekly" | "biweekly" | "monthly" | "quarterly" | "yearly">("none")
  const [recurrenceUntil, setRecurrenceUntil] = useState("")
  const [categoryId, setCategoryId] = useState("")
  // Von Hand gewaehlt (oder aus Entwurf/Vorgabe uebernommen)? Dann bleibt sie —
  // sonst folgt sie dem Vorgang (Wartung / „Angebot angenommen" → Umsetzung).
  const [kategorieAngefasst, setKategorieAngefasst] = useState(false)
  const [partnerIds, setPartnerIds] = useState<number[]>([])
  const [description, setDescription] = useState("")
  const [projectId, setProjectId] = useState("")
  // fortytools: Kunde + Objekt-Verknüpfung (statt HERO-Projekt)
  const [fortyCustomer, setFortyCustomer] = useState<{ id: number; name: string } | null>(null)
  const [fortyObjektPid, setFortyObjektPid] = useState("")  // Facility-project_id
  const [err, setErr] = useState<string | null>(null)
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null)

  // Draft-ID setzen wenn Modal aus gespeichertem Draft oeffnet, sonst nach Minimieren
  useEffect(() => {
    if (open && draftId) setCurrentDraftId(draftId)
    if (!open) setCurrentDraftId(null)
  }, [open, draftId])

  // Form NUR beim Öffnen initialisieren (open false→true). Die initial*-Props
  // werden im Parent immer VOR setCreateOpen(true) gesetzt, daher hier frisch
  // gelesen. Frühere Dep-Liste mit allen initial*-Werten konnte den Effekt
  // mitten in der Eingabe erneut feuern → Start/Ende (Uhrzeit) verschwand.
  useEffect(() => {
    if (open) {
      setTitle(initialTitle || "Neuer Termin")
      setStart(ensureDateTime(initialStart, "08:00") || toLocalInput(new Date().toISOString()))
      setEnd(ensureDateTime(initialEnd, "09:00") || toLocalInput(new Date(Date.now() + 2 * 3600_000).toISOString()))
      setAllDay(false)
      setRecurrence("none")
      setRecurrenceUntil("")
      setCategoryId(initialCategoryId || "")
      setKategorieAngefasst(!!initialCategoryId)
      setPartnerIds(initialPartnerIds || [])
      setDescription(initialDescription || "")
      setProjectId(initialProjectId || "")
      setFortyCustomer(null); setFortyObjektPid("")
      setErr(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const opts = useQuery<CalOptions>({
    // Vorgang im Schluessel: die Vorauswahl der Terminart haengt an ihm
    queryKey: ["cal-options", tenant, projectId || null],
    queryFn: async () => {
      const vorgang = projectId ? `&project_id=${encodeURIComponent(projectId)}` : ""
      const res = await fetch(`/api/v1/calendar/options?tenant=${encodeURIComponent(tenant)}${vorgang}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    enabled: open && !!tenant,
  })
  // fortytools: keine HERO-Kategorien/Projektverknüpfung — Termin lebt in
  // unserem Kalender (SSOT), das Büro überträgt ihn nach fortytools.
  const isForty = (opts.data as any)?.provider === "fortytools"

  // fortytools-Objekte (für die Objekt-Auswahl, gefiltert auf den gewählten Kunden)
  const fortyObjekte = useQuery<{ items: { id: number; project_id: number | null; name: string; customer_id: number; kunde_name: string | null; street: string | null }[] }>({
    queryKey: ["objekte", tenant],
    enabled: open && isForty && !!tenant,
    queryFn: async () => {
      const res = await fetch(`/api/v1/projects/objekte?tenant=${encodeURIComponent(tenant)}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })
  const kundenObjekte = (fortyObjekte.data?.items || []).filter((o) => !fortyCustomer || o.customer_id === fortyCustomer.id)

  // Vorauswahl, sobald die Kategorien geladen sind (verhindert HERO-Fehler
  // 'Bitte Kategorie wählen').
  //
  // 🔴 Die Kategorie des Mandanten gewinnt — NICHT die erste aus HEROs Liste.
  // Bei Sanitec steht dort "BLOCKER" ganz oben, und die Kategorie entscheidet,
  // in welchen Status der Vorgang nach dem Termin rutscht: KLI-827 (20.08.2026)
  // lag als "Beratung Klimaanlage" in "Umsetzung" und wurde deshalb auf
  // "Rechnung erstellen" geschoben statt auf "Angebot erstellen".
  // Nur wenn der Mandant keine gepflegt hat (oder HERO sie nicht mehr kennt),
  // faellt es auf die erste Kategorie zurueck.
  useEffect(() => {
    if (!open || kategorieAngefasst) return
    const kategorien = opts.data?.categories
    if (!kategorien?.length) return
    const vorgabe = opts.data?.default_category_id
    const bekannt = vorgabe != null && kategorien.some((c) => c.id === vorgabe)
    setCategoryId(String(bekannt ? vorgabe : kategorien[0].id))
  }, [open, opts.data, kategorieAngefasst])

  const create = useMutation({
    mutationFn: async () => {
      // All-Day: start=00:00, end=23:59 des gewaehlten Tages — mit lokalem TZ-Offset
      // damit UTC-Konvertierung nicht in den Vortag rutscht.
      let sIso: string, eIso: string
      if (allDay) {
        const sDay = start.slice(0, 10) || new Date().toISOString().slice(0, 10)
        const eDay = (end || start).slice(0, 10) || sDay
        const tzOffset = tzOffsetStr(new Date(`${sDay}T12:00:00`))
        sIso = `${sDay}T00:00:00${tzOffset}`
        eIso = `${eDay}T23:59:00${tzOffset}`
      } else {
        // start/end kommen als naive lokale Strings ("2026-05-04T14:30") aus
        // dem datetime-local-Input. .toISOString() wuerde sie nach UTC
        // konvertieren — HERO ignoriert dann das Z-Suffix und speichert die
        // UTC-Stunden als Berlin-Zeit → 2h-Versatz im Sommer (Bug aus Memory:
        // "HERO sendet Berlin-Stunden mit fake +00:00"). Fix: lokalen
        // TZ-Offset explizit anhaengen, dann ist das ISO eindeutig.
        const sOff = tzOffsetStr(new Date(start))
        const eOff = tzOffsetStr(new Date(end))
        sIso = `${start}:00${sOff}`
        eIso = `${end}:00${eOff}`
      }
      const payload: any = {
        tenant, title,
        start: sIso, end: eIso,
        all_day: allDay,
        category_id: categoryId ? parseInt(categoryId) : null,
        partner_ids: partnerIds,
        description,
        project_match_id: projectId ? parseInt(projectId) : null,
      }
      if (isForty && fortyObjektPid) payload.forty_project_id = parseInt(fortyObjektPid)
      if (recurrence !== "none" && recurrenceUntil) {
        payload.recurrence = { freq: recurrence, until: recurrenceUntil }
      }
      const res = await fetch("/api/v1/calendar/events", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || `HTTP ${res.status}`)
      return json
    },
    onSuccess: (json: any) => {
      qc.invalidateQueries({ queryKey: ["calendar"] })
      // Draft entfernen (Termin wurde erfolgreich erstellt)
      if (currentDraftId) removeDraft(currentDraftId)
      // Gekappte Serie sichtbar machen — sonst faellt erst Wochen spaeter auf,
      // dass die Serie frueher endet als angefordert.
      if (json?.series_capped) {
        toast(
          `Serie auf ${json.series_instances} Termine begrenzt — letzter Termin ` +
          `${json.series_last || "?"}. Für einen längeren Zeitraum bitte eine ` +
          `zweite Serie ab diesem Datum anlegen.`,
          "error",
        )
      }
      onClose()
    },
    onError: (e) => setErr(e instanceof Error ? e.message : String(e)),
  })

  // Minimieren = aktuelle Form in Draft-Store schreiben + Modal schliessen.
  // Die Pill wird dann global in AppLayout/MinimizedDraftsBar gerendert.
  const handleMinimize = () => {
    const id = currentDraftId || newDraftId()
    const draft: CalendarDraft = {
      kind: "calendar",
      id,
      created_at: Date.now(),
      tenant,
      title: title || "",
      start: start || "",
      end: end || "",
      allDay,
      categoryId: categoryId || "",
      partnerIds: partnerIds.length ? partnerIds : undefined,
      description: description || "",
      projectId: projectId || "",
    }
    saveDraft(draft)
    setCurrentDraftId(id)
    onClose()
  }

  if (!open) return null

  return (
    <div
      className="pp-overlay"
      data-portal-modal
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.4)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
      onMouseDown={(ev) => { if (ev.target === ev.currentTarget) onClose() }}
    >
      <form
        onSubmit={async (ev) => {
          ev.preventDefault(); setErr(null)
          // #148 (Paul: „nur warnen"): der Kalender-Plus bucht genau die
          // gewaehlten Mitarbeiter — ohne Auswahl steht der Termin bei niemandem.
          if (!(await darfTerminOhneMitarbeiterBuchen(partnerIds.length))) return
          create.mutate()
        }}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12,
          width: 560, maxWidth: "95vw", maxHeight: "90vh", overflow: "hidden",
          display: "flex", flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header mit Minimieren + Close */}
        <div style={{
          padding: "16px 24px", borderBottom: "1px solid #e5e7eb",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: "#1a1a2e" }}>Neuer Termin</h3>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              type="button"
              onClick={handleMinimize}
              title="Minimieren"
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "#666", padding: "4px 8px", borderRadius: 4,
                display: "inline-flex", alignItems: "center",
              }}
            >
              <Minimize2 className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "none", border: "none",
                fontSize: 22, cursor: "pointer", color: "#666",
                padding: "4px 8px", borderRadius: 4,
              }}
            >×</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: "20px 24px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
          {err && <div className="pp-alert pp-alert-err">{err}</div>}

          {isForty ? (
            <div>
              <label style={labelStyle}>Titel</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 12 }}>
              <div>
                <label style={labelStyle}>Kategorie *</label>
                <select value={categoryId} onChange={(e) => { setCategoryId(e.target.value); setKategorieAngefasst(true) }} required>
                  <option value="">– Kategorie wählen –</option>
                  {/* 🔑 Der vorausgewaehlte Wert MUSS als eigene Option dabei
                      sein. `categoryId` kommt aus `default_category_id` des
                      Mandanten; kennt die geladene Liste diese ID nicht, faellt
                      das Feld auf den Platzhalter zurueck und behauptet „keine
                      Kategorie", obwohl eine gesetzt ist. Dieselbe Falle wie
                      beim Mandanten- und beim Zustaendig-Feld. */}
                  {categoryId && !(opts.data?.categories || []).some((c) => String(c.id) === categoryId) && (
                    <option value={categoryId}>Vorgabe des Mandanten (#{categoryId})</option>
                  )}
                  {opts.data?.categories?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Titel</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} required />
              </div>
            </div>
          )}

          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
            {/* allDay aendert NUR den Input-Typ. Der State haelt start/end immer
                als volles "YYYY-MM-DDTHH:mm" (siehe withDate/mergeDate) → die
                Uhrzeit geht beim Umschalten nie verloren (Bug 15.06.2026). */}
            <input type="checkbox" checked={allDay} onChange={(e) => setAllDay(e.target.checked)} />
            <span style={{ fontWeight: 600, color: "#555" }}>Ganztägig</span>
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Start</label>
              <input
                type={allDay ? "date" : "datetime-local"}
                value={allDay ? start.slice(0, 10) : start}
                onChange={(e) => {
                  // Im allDay-Modus liefert das date-Feld nur "YYYY-MM-DD" — wir
                  // mergen die bestehende Uhrzeit zurueck, damit sie erhalten bleibt.
                  const newStart = mergeDate(e.target.value, start, "08:00")
                  setStart(newStart)
                  // Wenn neues Start nach Ende liegt: Ende auf Start + Dauer setzen
                  // (Default-Dauer = bisherige Differenz, sonst 1h).
                  try {
                    if (newStart && end) {
                      const ns = new Date(newStart)
                      const en = new Date(end)
                      if (!isNaN(ns.getTime()) && !isNaN(en.getTime()) && ns >= en) {
                        const prevStart = new Date(start)
                        const durMs = !isNaN(prevStart.getTime()) && en > prevStart
                          ? en.getTime() - prevStart.getTime()
                          : 60 * 60 * 1000
                        const newEnd = new Date(ns.getTime() + durMs)
                        const pad = (n: number) => String(n).padStart(2, "0")
                        setEnd(`${newEnd.getFullYear()}-${pad(newEnd.getMonth() + 1)}-${pad(newEnd.getDate())}T${pad(newEnd.getHours())}:${pad(newEnd.getMinutes())}`)
                      }
                    }
                  } catch { /* ignore */ }
                }}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Ende</label>
              <input
                type={allDay ? "date" : "datetime-local"}
                value={allDay ? end.slice(0, 10) : end}
                onChange={(e) => setEnd(mergeDate(e.target.value, end, "09:00"))}
                required
              />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={labelStyle}>Wiederholung</label>
              <select value={recurrence} onChange={(e) => setRecurrence(e.target.value as any)}>
                <option value="none">Keine</option>
                <option value="daily">Täglich (inkl. Wochenende)</option>
                <option value="weekdays">Werktags (Mo–Fr)</option>
                <option value="weekly">Wöchentlich</option>
                <option value="biweekly">14-tägig</option>
                <option value="monthly">Monatlich</option>
                <option value="quarterly">Vierteljährlich</option>
                <option value="yearly">Jährlich</option>
              </select>
            </div>
            {recurrence !== "none" && (
              <div>
                <label style={labelStyle}>Bis einschließlich</label>
                <input type="date" value={recurrenceUntil} onChange={(e) => setRecurrenceUntil(e.target.value)} required />
              </div>
            )}
          </div>

          {(opts.data?.partners?.length || opts.data?.monteure?.length) && (
            <div>
              <label style={labelStyle}>Mitarbeiter</label>
              <div style={{ maxHeight: 120, overflowY: "auto", border: "1px solid var(--border-color)", borderRadius: 6, padding: 8 }}>
                {[...(opts.data?.partners || []), ...(opts.data?.monteure || [])]
                  .filter((p, i, arr) => arr.findIndex((x) => x.id === p.id || x.name === p.name) === i)
                  .map((p) => (
                    <label key={p.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 2, cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={partnerIds.includes(p.id)}
                        onChange={(e) => setPartnerIds(
                          e.target.checked ? [...partnerIds, p.id] : partnerIds.filter((x) => x !== p.id),
                        )}
                      />
                      {p.name}
                    </label>
                  ))}
              </div>
            </div>
          )}

          {!isForty && (
            <ProjectSearch
              tenant={tenant}
              projectId={projectId}
              onChange={setProjectId}
            />
          )}
          {isForty && (
            <FortyKundeObjektPicker
              tenant={tenant}
              objekte={kundenObjekte}
              customer={fortyCustomer}
              objektPid={fortyObjektPid}
              onCustomer={(c) => { setFortyCustomer(c); setFortyObjektPid("") }}
              onObjekt={setFortyObjektPid}
              defaultTitleWasUsed={title === "Neuer Termin"}
              onTitleSuggest={(t) => { if (title === "Neuer Termin") setTitle(t) }}
            />
          )}
          {isForty && (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
              fortytools-Termin: wird in unserem Kalender angelegt (als „⚠ offen"),
              das Büro überträgt ihn nach fortytools. Keine Kategorie/Projekt.
            </div>
          )}

          <div>
            <label style={labelStyle}>Beschreibung</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 24px", borderTop: "1px solid #e5e7eb",
          display: "flex", justifyContent: "flex-end", gap: 10,
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 18px", border: "1px solid #d1d5db",
              background: "#fff", borderRadius: 6, cursor: "pointer", fontSize: 14,
              fontFamily: "inherit",
            }}
          >Abbrechen</button>
          <button
            type="submit"
            disabled={!title || !start || !end || create.isPending}
            className={cn(create.isPending && "opacity-50")}
            style={{
              padding: "8px 18px", background: "#3c4e7e", color: "#fff",
              border: "none", borderRadius: 6, cursor: "pointer",
              fontSize: 14, fontWeight: 600, fontFamily: "inherit",
            }}
          >{create.isPending ? "Erstellt..." : "Termin erstellen"}</button>
        </div>
      </form>
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: "#555", display: "block", marginBottom: 4,
}

function ProjectSearch({
  tenant, projectId, onChange,
}: {
  tenant: string
  projectId: string
  onChange: (id: string) => void
}) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<Array<{ id: number; name: string; status?: string; contact_name?: string }>>([])
  const [createOpen, setCreateOpen] = useState(false)
  const [createUseExisting, setCreateUseExisting] = useState(false)

  const doSearch = async (q: string) => {
    if (q.length < 2) { setResults([]); return }
    try {
      const url = `/api/v1/projects/search?tenant=${encodeURIComponent(tenant)}&q=${encodeURIComponent(q)}`
      const r = await fetch(url, { credentials: "include" })
      const d = await r.json()
      setResults(d.items || [])
    } catch { setResults([]) }
  }

  return (
    <div>
      <label style={labelStyle}>Projekt-Verknüpfung (optional)</label>
      {projectId ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px",
                      background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
          <HeroInfoPopover tenantSlug={tenant} projectId={projectId}>
            <span style={{ flex: 1, fontSize: 12.5, cursor: "help", color: "var(--accent-color)" }}>
              <HeroVorgangName tenantSlug={tenant} projectId={projectId} />
            </span>
          </HeroInfoPopover>
          <span style={{ flex: 1 }} />
          <button type="button" onClick={() => onChange("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}>✕</button>
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); doSearch(e.target.value) }}
              placeholder="Projekt suchen..."
              style={{ flex: "1 1 200px" }}
            />
            <button
              type="button"
              onClick={() => { setCreateUseExisting(false); setCreateOpen(true) }}
              style={{
                background: "var(--accent-color, #3C3489)", color: "#fff", border: "none",
                padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title="Neues Projekt + neuer Kontakt in HERO anlegen"
            >+ Neu</button>
            <button
              type="button"
              onClick={() => { setCreateUseExisting(true); setCreateOpen(true) }}
              style={{
                background: "#fff", color: "var(--accent-color, #3C3489)",
                border: "1px solid var(--accent-color, #3C3489)",
                padding: "6px 12px", borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: "pointer",
                whiteSpace: "nowrap",
              }}
              title="Neues Projekt für einen bereits existierenden HERO-Kontakt anlegen"
            >+ Für bestehenden Kontakt</button>
          </div>
          {results.length > 0 && (
            <div style={{
              marginTop: 4, maxHeight: 140, overflowY: "auto",
              border: "1px solid var(--border-color)", borderRadius: 6,
            }}>
              {results.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => { onChange(String(p.id)); setQuery(""); setResults([]) }}
                  style={{
                    width: "100%", textAlign: "left", padding: "8px 10px",
                    borderBottom: "1px solid var(--border-color)",
                    background: "none", border: "none", cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: "#999" }}>
                    {p.status || "kein Status"} · ID {p.id}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      <CreateContactModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        mode="calendar"
        itemId={0}
        tenantSlug={tenant}
        initialUseExistingContact={createUseExisting}
        onSuccess={(result) => {
          if (result?.project_id) onChange(String(result.project_id))
          setCreateOpen(false)
        }}
      />
    </div>
  )
}

function tzOffsetStr(d: Date): string {
  const off = -d.getTimezoneOffset()
  const sign = off >= 0 ? "+" : "-"
  const h = String(Math.floor(Math.abs(off) / 60)).padStart(2, "0")
  const m = String(Math.abs(off) % 60).padStart(2, "0")
  return `${sign}${h}:${m}`
}

function toLocalInput(iso: string): string {
  try {
    const d = new Date(iso)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, "0")
    const day = String(d.getDate()).padStart(2, "0")
    const h = String(d.getHours()).padStart(2, "0")
    const min = String(d.getMinutes()).padStart(2, "0")
    return `${y}-${m}-${day}T${h}:${min}`
  } catch { return "" }
}

/** Mergt einen (evtl. nur Datum-)Input mit der bestehenden Uhrzeit zu einem
 *  vollen "YYYY-MM-DDTHH:mm". Leerer Input → "". So bleibt die Uhrzeit beim
 *  Umschalten zwischen date- und datetime-local-Feld erhalten. */
function mergeDate(input: string, prev: string, fallbackTime: string): string {
  if (!input) return ""
  if (input.includes("T")) return input.slice(0, 16)  // schon volles datetime
  const time = (prev.match(/T(\d{2}:\d{2})/)?.[1]) || fallbackTime
  return `${input}T${time}`
}

/** Stellt sicher, dass ein Start/Ende-Wert ein volles "YYYY-MM-DDTHH:mm" ist. */
function ensureDateTime(v: string | undefined, fallbackTime: string): string {
  if (!v) return ""
  if (v.includes("T")) return v.slice(0, 16)
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T${fallbackTime}`
  return v
}

const _lblStyle: React.CSSProperties = { display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4, color: "var(--text-secondary)" }

/** fortytools: Kunde suchen + Objekt (Facility) dieses Kunden wählen. */
export function FortyKundeObjektPicker({
  tenant, objekte, customer, objektPid, onCustomer, onObjekt, onTitleSuggest,
}: {
  tenant: string
  objekte: { id: number; project_id: number | null; name: string; customer_id: number; kunde_name: string | null; street: string | null }[]
  customer: { id: number; name: string } | null
  objektPid: string
  onCustomer: (c: { id: number; name: string } | null) => void
  onObjekt: (projectId: string) => void
  onTitleSuggest: (t: string) => void
  defaultTitleWasUsed: boolean
}) {
  const [q, setQ] = useState("")
  const [hits, setHits] = useState<{ id: number; name: string }[]>([])
  const [searching, setSearching] = useState(false)
  const deb = useRef<number | null>(null)

  const search = (text: string) => {
    setQ(text)
    if (deb.current) window.clearTimeout(deb.current)
    const s = text.trim()
    if (!tenant || s.length < 2) { setHits([]); return }
    setSearching(true)
    deb.current = window.setTimeout(async () => {
      try {
        const r = await fetch(`/api/v1/contacts?tenant=${encodeURIComponent(tenant)}&q=${encodeURIComponent(s)}&limit=20`, { credentials: "include" })
        const d = await r.json()
        setHits((d.items || []).map((i: any) => ({
          id: i.id,
          name: [i.first_name, i.last_name].filter(Boolean).join(" ") || i.company_name || `Kunde #${i.id}`,
        })))
      } catch { setHits([]) } finally { setSearching(false) }
    }, 250)
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <div>
        <label style={_lblStyle}>Kunde</label>
        {customer ? (
          <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 6 }}>
            <span style={{ flex: 1, fontSize: 12.5 }}>{customer.name}</span>
            <button type="button" onClick={() => onCustomer(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#dc2626" }}>✕</button>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            <input value={q} onChange={(e) => search(e.target.value)} placeholder="Kunde suchen (min. 2 Zeichen)" style={{ width: "100%" }} />
            {searching && <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "2px 0" }}>Suche…</div>}
            {hits.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, background: "var(--bg-card)", border: "1px solid var(--border-color)", borderRadius: 6, marginTop: 2, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
                {hits.map((c) => (
                  <button key={c.id} type="button" onMouseDown={(e) => { e.preventDefault(); onCustomer(c); setQ(""); setHits([]) }}
                    style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 10px", background: "none", border: "none", cursor: "pointer", fontSize: 12.5 }}
                    className="hover:bg-secondary">{c.name}</button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <div>
        <label style={_lblStyle}>Objekt</label>
        <select
          value={objektPid}
          onChange={(e) => {
            onObjekt(e.target.value)
            const o = objekte.find((x) => String(x.project_id) === e.target.value)
            if (o) onTitleSuggest([o.kunde_name, o.name].filter(Boolean).join(" · "))
          }}
          disabled={!customer}
        >
          <option value="">{customer ? (objekte.length ? "– Objekt wählen –" : "Keine Objekte") : "Zuerst Kunde wählen"}</option>
          {objekte.filter((o) => o.project_id != null).map((o) => (
            <option key={o.id} value={String(o.project_id)}>{o.name}{o.street ? ` · ${o.street}` : ""}</option>
          ))}
        </select>
      </div>
    </div>
  )
}
